/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/system/PullToRefreshOverlay.tsx
 *
 * Rôle :
 *   Indicateur visuel pour le pull-to-refresh (`usePullToRefresh`).
 *   Affiche une flèche/spinner en haut de l'écran qui suit le
 *   geste de l'utilisateur, et bascule en spinner « ⟳ » quand le
 *   seuil de refresh est atteint.
 *
 *   Monté UNE SEULE FOIS dans AppLayout → couvre toute l'app.
 *   Demande Mika 2026-05-26 (J-1), cf. CLAUDE.md §5.
 *
 * Palette : tokens TNE Vosges (`--tne-color-primary` rouge profond).
 * Accessibilité : `role="status"` + `aria-live="polite"` quand actif.
 *
 * @author Mika
 * @since 2026-05-26
 */

import type { JSX } from 'react';
import { usePullToRefresh, REFRESH_THRESHOLD } from '@/hooks/usePullToRefresh';
import styles from './PullToRefreshOverlay.module.css';

export function PullToRefreshOverlay(): JSX.Element | null {
    const { pull, canRefresh, refreshing } = usePullToRefresh();

    if (pull <= 0 && !refreshing) return null;

    const visibleY = Math.min(pull, 120);
    const opacity = Math.min(1, pull / REFRESH_THRESHOLD);
    const rotation = Math.min(180, (pull / REFRESH_THRESHOLD) * 180);

    return (
        <div
            className={styles.overlay}
            style={{
                transform: `translateY(${visibleY}px)`,
                opacity,
            }}
            role="status"
            aria-live="polite"
            aria-label={refreshing
                ? 'Actualisation en cours…'
                : canRefresh
                ? 'Relâchez pour actualiser'
                : 'Tirez pour actualiser'}
        >
            <div className={`${styles.indicator} ${refreshing ? styles.spinning : ''}`}>
                {refreshing ? (
                    <span aria-hidden="true" className={styles.spinner}>⟳</span>
                ) : (
                    <span
                        aria-hidden="true"
                        className={styles.arrow}
                        style={{ transform: `rotate(${rotation}deg)` }}
                    >↓</span>
                )}
            </div>
        </div>
    );
}
